import { useAbility } from 'permissions/hooks/useAbility';
import { useCallback, useMemo, useState } from 'react';
import { Action, HeaderAction } from '../models/configurableTable';

type UseDataGridDialogsProps<T> = {
  actions?: Action[];
  headerActions?: HeaderAction<T>[];
};

export const useDataGridDialogs = <T>({
  actions = [],
  headerActions = [],
}: UseDataGridDialogsProps<T>) => {
  const ability = useAbility();

  const dialogActions = useMemo(() => {
    const allActions = [...actions, ...headerActions];

    if (!allActions) {
      return [];
    }

    const availableActions = allActions?.filter((action) => {
      if (action.type !== 'dialog') {
        return false;
      }

      if (
        action.permission &&
        ability.cannot(action.permission.action, action.permission.subject)
      ) {
        return false;
      }

      return true;
    });

    return availableActions;
  }, []);

  const [dialogs, setDialogs] = useState(() => {
    return dialogActions?.reduce((dialogs: any, currentValue: any) => {
      return {
        ...dialogs,
        [currentValue.id]: null,
      };
    }, {});
  });

  const selectDialog = useCallback(
    (dialogId: string, row: any) => {
      setDialogs((currentDialogs: any) => ({
        ...currentDialogs,
        [dialogId]: row,
      }));
    },
    [setDialogs],
  );

  const unselectDialog = useCallback(
    (dialogId: string) => {
      setDialogs((currentDialogs: any) => ({
        ...currentDialogs,
        [dialogId]: null,
      }));
    },
    [setDialogs],
  );

  return {
    dialogActions,
    dialogs,
    selectDialog,
    unselectDialog,
  };
};
